import { createElement, lazy, useState, type ComponentType } from "react";

/**
 * `React.lazy` with a `preload()` that actually saves the wait.
 *
 * Calling `import()` ahead of time is not enough on its own. A `lazy()`
 * component knows nothing of a fetch it did not start: on its first render it
 * calls its loader, gets a promise — pending for at least a microtask even
 * when the module is already in memory — and SUSPENDS. React then shows the
 * nearest fallback and, when the promise settles, holds the real content back
 * until 300ms have passed since that fallback appeared (its throttle on
 * revealing suspended content, FALLBACK_THROTTLE_MS). Measured on Platform
 * Settings: with both chunks already fetched, the panel still took ~350ms
 * from the tap to its first frame, and a warm section still flashed its
 * "Loading…" fallback.
 *
 * Here `preload()` and the lazy share one promise, and once it has resolved
 * the wrapper renders the module's component directly — no suspension, so no
 * fallback and no throttle. The choice is made once per mounted instance, so
 * a component that did suspend keeps rendering through the same `lazy`
 * element and is never remounted when the module lands.
 */
export type Preloadable<P = any> = ComponentType<P> & { preload: () => Promise<unknown> };

export function lazyPreload<P = any>(load: () => Promise<{ default: ComponentType<any> }>): Preloadable<P> {
  let loaded: ComponentType<P> | undefined;
  let pending: Promise<{ default: ComponentType<P> }> | undefined;

  const preload = () => {
    pending ??= load().then(
      (mod) => {
        loaded = mod.default;
        return mod;
      },
      (err) => {
        // A failed fetch must be retryable — by the next preload, or by the
        // route's own render, whose ChunkErrorBoundary reports it.
        pending = undefined;
        throw err;
      },
    );
    return pending;
  };

  const Lazy = lazy(preload);

  function LazyPreload(props: P) {
    const [Impl] = useState<ComponentType<P>>(() => loaded ?? (Lazy as unknown as ComponentType<P>));
    return createElement(Impl as ComponentType<object>, props as object);
  }

  return Object.assign(LazyPreload, { preload }) as Preloadable<P>;
}

/**
 * Run `fn` once the page has been idle a moment. Safari has no
 * requestIdleCallback, so it falls back to a plain delay — long enough that a
 * cold start's own chunks and data are not competing with the warm-up.
 */
export function whenIdle(fn: () => void, fallbackMs = 2500): () => void {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(fn, { timeout: fallbackMs * 2 });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(fn, fallbackMs);
  return () => window.clearTimeout(id);
}

type DeviceHints = { deviceMemory?: number; connection?: { saveData?: boolean } | null };

/**
 * Whether to fetch pages nobody has asked for yet. Not on a device that says
 * it has 4 GB or less (a Chromebook, an older NUC: every warmed chunk is
 * parsed and kept in memory for the life of the tab), nor when the reader has
 * asked the browser to save data. A page is then fetched when it is opened,
 * as it always is on a cold visit. Safari reports neither, so it still warms.
 */
export function canWarmIdle(nav: DeviceHints | undefined = typeof navigator === "undefined" ? undefined : (navigator as unknown as DeviceHints)): boolean {
  if (!nav) return false;
  if (typeof nav.deviceMemory === "number" && nav.deviceMemory <= 4) return false;
  if (nav.connection?.saveData) return false;
  return true;
}
